import { useEffect, useRef, type FocusEvent, type MouseEvent } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { HeartIcon, HomeIcon, SearchIcon } from '@features/shared/icons/icons'
import { Path } from '@/routes/paths'
import { AccountAvatar, AccountName } from '@features/auth/components/AccountDrawer'
import { usePermissionBypassStore } from '@features/permissions/stores/usePermissionBypassStore'
import { RAIL_MODULE_ATTRIBUTE } from '../logic/railAnchor'
import { RAIL_NAME_CARD_ATTRIBUTE, type RailName } from './RailNameCard'
import {
  RAIL_AIM_REST_MS,
  RAIL_HOVER_INTENT_MS,
  RAIL_REST_TOLERANCE_PX,
} from '@/styling/tokens.interaction'
import {
  addPointerSample,
  isAimingAtMenu,
  pointerSampleBefore,
  type MenuEdge,
  type PointerSample,
} from '../logic/menuAim'
import { isKeyboardClick } from '@features/shared/hooks/useClickIntent'
import type { AppNavigationModule } from '../../../config/appNavigation'
import { MenuDescription } from './MenuDescription'
import SidebarModeControl from './SidebarModeControl'
import { FAVORITES_RAIL_ITEM, SEARCH_RAIL_ITEM, type ActiveModule } from '../models'

interface AppSidebarProps {
  modules: readonly AppNavigationModule[]
  selectedModule: ActiveModule
  hoveredModule: ActiveModule | null
  onModuleHover: (module: ActiveModule | null) => void
  /**
   * An item with a menu was clicked: its menu opens at once, ready for the
   * keyboard. The AI Assistant's pointer click starts a new chat instead.
   */
  onModuleClick: (module: ActiveModule, click: { keyboard: boolean }) => void
  /** Names show beside the icons; collapsed, the rail is icons only. */
  isExpanded: boolean
  /**
   * A menu (pinned or hovered) is open beside the rail: the pointer heading
   * for it leaves it as it is until it rests (`RAIL_AIM_REST_MS`); scanning
   * the rail switches it at once.
   */
  isMenuShown?: boolean
  /** Where the open menu's card is, for the pointer's aim; null when none shows. */
  getMenuEdge?: () => MenuEdge | null
  /** The item without a menu the pointer rests on (its name may show in a RailNameCard), or null. */
  onRailNameChange: (name: RailName | null) => void
  /** Keep the previous card while this item's hover intent is pending. */
  onRailNameEnter?: () => void
  /** The pointer or focus left an item without a menu: its name, if still shown, goes. */
  onRailNameLeave: (anchor: string) => void
  /** Home or AI, while its name shows beside the rail: its row looks open. */
  namedItem: string | null
  /** The sidebar's search (SidebarSearch) is open. */
  searchOpen: boolean
  /** Search was clicked (or pressed): hold it open with the cursor in it, or close a held one. */
  onSearchClick: () => void
  onExpansionOpenChange?: (open: boolean) => void
}

const HOVER_INTENT_ZONE_PX = 32

/** Who's signed in, marked in warning amber while they bypass permissions. */
function MeAvatar() {
  const bypassing = usePermissionBypassStore((state) => state.bypassing)
  return (
    <span className="relative flex">
      <AccountAvatar />
      {bypassing && (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[color:var(--text-warning)]"
        />
      )}
    </span>
  )
}

/** The Me item's name, saying so to a screen reader while permissions are bypassed. */
function MeName() {
  const bypassing = usePermissionBypassStore((state) => state.bypassing)
  return (
    <>
      <AccountName />
      {bypassing && <span className="sr-only"> (bypassing permissions)</span>}
    </>
  )
}
type HoverTarget = ActiveModule | typeof SEARCH_RAIL_ITEM | 'home' | 'buffer'

export default function AppSidebar({
  modules,
  selectedModule,
  hoveredModule,
  onModuleHover,
  onModuleClick,
  isExpanded,
  isMenuShown = false,
  getMenuEdge,
  onRailNameChange,
  onRailNameEnter,
  onRailNameLeave,
  namedItem,
  searchOpen,
  onSearchClick,
  onExpansionOpenChange,
}: AppSidebarProps) {
  const hoverIntentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingModuleRef = useRef<ActiveModule | null>(null)
  // Where the pointer was when the pending switch began waiting for it to rest.
  const restAnchorRef = useRef<{ x: number; y: number } | null>(null)
  const hoverTargetRightEdgeRef = useRef<Partial<Record<HoverTarget, number | null>>>({
    buffer: null,
  })

  // Where the pointer has just been over the rail, for the way it's heading.
  const pointerTrailRef = useRef<PointerSample[]>([])
  const recordPointer = (event: MouseEvent<HTMLElement>) => {
    pointerTrailRef.current = addPointerSample(pointerTrailRef.current, {
      x: event.clientX,
      y: event.clientY,
      t: event.timeStamp,
    })
  }
  const isHeadingForMenu = (event: MouseEvent<HTMLElement>) => {
    const menu = getMenuEdge?.()
    if (!menu) return false
    const from = pointerSampleBefore(pointerTrailRef.current, event.timeStamp)
    return from !== null && isAimingAtMenu(from, { x: event.clientX, y: event.clientY }, menu)
  }

  const isSelected = (module: ActiveModule) => selectedModule === module
  const isHovered = (module: ActiveModule) => hoveredModule === module

  const clearHoverIntent = () => {
    if (hoverIntentTimeoutRef.current !== null) {
      clearTimeout(hoverIntentTimeoutRef.current)
      hoverIntentTimeoutRef.current = null
    }
    pendingModuleRef.current = null
    restAnchorRef.current = null
  }

  const isStillResting = (event: MouseEvent<HTMLElement>) => {
    const anchor = restAnchorRef.current
    return (
      anchor !== null &&
      Math.abs(event.clientX - anchor.x) <= RAIL_REST_TOLERANCE_PX &&
      Math.abs(event.clientY - anchor.y) <= RAIL_REST_TOLERANCE_PX
    )
  }

  const startRest = (event: MouseEvent<HTMLElement>) => {
    restAnchorRef.current = { x: event.clientX, y: event.clientY }
  }

  const activateModule = (module: ActiveModule) => {
    clearHoverIntent()
    if (hoveredModule !== module) onModuleHover(module)
  }

  const scheduleModuleHover = (module: ActiveModule) => {
    if (hoveredModule === module || pendingModuleRef.current === module) return

    clearHoverIntent()
    pendingModuleRef.current = module
    hoverIntentTimeoutRef.current = setTimeout(() => {
      hoverIntentTimeoutRef.current = null
      pendingModuleRef.current = null
      onModuleHover(module)
    }, RAIL_HOVER_INTENT_MS)
  }

  // Heading for the open menu, the pointer switches it only once it rests on
  // an item; any travel starts the wait over.
  const scheduleModuleRest = (module: ActiveModule, event: MouseEvent<HTMLElement>) => {
    if (hoveredModule === module) {
      clearHoverIntent()
      return
    }
    if (pendingModuleRef.current === module && isStillResting(event)) return

    clearHoverIntent()
    pendingModuleRef.current = module
    startRest(event)
    hoverIntentTimeoutRef.current = setTimeout(() => {
      hoverIntentTimeoutRef.current = null
      pendingModuleRef.current = null
      restAnchorRef.current = null
      onModuleHover(module)
    }, RAIL_AIM_REST_MS)
  }

  const scheduleHoverClear = () => {
    if (hoveredModule === null || pendingModuleRef.current === null) clearHoverIntent()
    if (hoveredModule === null) return

    clearHoverIntent()
    hoverIntentTimeoutRef.current = setTimeout(() => {
      hoverIntentTimeoutRef.current = null
      onModuleHover(null)
    }, RAIL_HOVER_INTENT_MS)
  }

  const cacheHoverTargetBounds = (target: HoverTarget, event: MouseEvent<HTMLElement>) => {
    hoverTargetRightEdgeRef.current[target] = event.currentTarget.getBoundingClientRect().right
  }

  const clearHoverTargetBounds = () => {
    hoverTargetRightEdgeRef.current = {
      buffer: null,
    }
  }

  const isInHoverIntentZone = (target: HoverTarget, event: MouseEvent<HTMLElement>) => {
    const rightEdge =
      hoverTargetRightEdgeRef.current[target] ?? event.currentTarget.getBoundingClientRect().right

    hoverTargetRightEdgeRef.current[target] = rightEdge
    return rightEdge - event.clientX <= HOVER_INTENT_ZONE_PX
  }

  const handleModulePointer = (module: ActiveModule, event: MouseEvent<HTMLElement>) => {
    // A menu is open: heading for it, wait for a rest; scanning, switch now.
    if (isMenuShown) {
      if (isHeadingForMenu(event)) scheduleModuleRest(module, event)
      else activateModule(module)
      return
    }

    if (!isExpanded) {
      activateModule(module)
      return
    }

    if (isInHoverIntentZone(module, event)) {
      scheduleModuleHover(module)
      return
    }

    activateModule(module)
  }

  // Only the empty space above Me extends its hover area, so diagonal travel
  // into its bottom-aligned menu never clears it. Group dividers still close
  // menus normally, and actual rail items keep their usual hover behavior.
  const handleGapPointer = (event: MouseEvent<HTMLElement>, keepMeOpen: boolean) => {
    if (keepMeOpen && hoveredModule === 'me') {
      clearHoverIntent()
      return
    }
    // Restarted by every move, so only a rest here closes the menu.
    if (isMenuShown) {
      scheduleHoverClear()
      return
    }
    if (!isExpanded) {
      onModuleHover(null)
      return
    }

    if (isInHoverIntentZone('buffer', event)) {
      scheduleHoverClear()
      return
    }

    clearHoverIntent()
    onModuleHover(null)
  }

  useEffect(() => {
    if (!isExpanded) clearHoverIntent()
    clearHoverTargetBounds()
  }, [isExpanded])

  useEffect(() => clearHoverIntent, [])

  useEffect(() => {
    const handleResize = () => clearHoverTargetBounds()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const railNameTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRailNameRef = useRef<string | null>(null)
  const activeRailNameRef = useRef<string | null>(null)
  const clearRailNameTimeout = () => {
    if (railNameTimeoutRef.current !== null) {
      clearTimeout(railNameTimeoutRef.current)
      railNameTimeoutRef.current = null
    }
    pendingRailNameRef.current = null
    restAnchorRef.current = null
  }
  // Switch the old menu and the item's own card together. Use the same
  // immediate icon hover / right-edge intent as items with menus, rather
  // than adding a separate delay to Home and Search.
  const activateRailName = (name: RailName) => {
    clearRailNameTimeout()
    activeRailNameRef.current = name.anchor
    onModuleHover(null)
    onRailNameChange(name)
  }
  const handleRailNamePointer = (
    name: RailName & { anchor: HoverTarget },
    event: MouseEvent<HTMLElement>
  ) => {
    if (activeRailNameRef.current === name.anchor) return
    // Over an open menu, heading for it, the name (and the menu's blanking)
    // waits for a rest; scanning, it shows at once.
    if (isMenuShown) {
      if (!isHeadingForMenu(event)) {
        activateRailName(name)
        return
      }
      if (pendingRailNameRef.current === name.anchor && isStillResting(event)) return
      clearRailNameTimeout()
      pendingRailNameRef.current = name.anchor
      startRest(event)
      railNameTimeoutRef.current = setTimeout(() => activateRailName(name), RAIL_AIM_REST_MS)
      return
    }
    if (!isExpanded || !isInHoverIntentZone(name.anchor, event)) {
      activateRailName(name)
      return
    }
    if (pendingRailNameRef.current === name.anchor) return
    clearRailNameTimeout()
    pendingRailNameRef.current = name.anchor
    railNameTimeoutRef.current = setTimeout(() => activateRailName(name), RAIL_HOVER_INTENT_MS)
  }
  const isInNameCard = (target: EventTarget | null) =>
    target instanceof Element && target.closest(`[${RAIL_NAME_CARD_ATTRIBUTE}]`) !== null
  const menulessItemHandlers = (
    anchor: Exclude<HoverTarget, 'buffer'>,
    label: string,
    // Search opens its card on a rest, so keyboard focus (Escape hands it
    // back there) mustn't open it again: Enter does.
    { nameOnFocus = true }: { nameOnFocus?: boolean } = {}
  ) => ({
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      onRailNameEnter?.()
      clearHoverIntent()
      clearRailNameTimeout()
      activeRailNameRef.current = null
      cacheHoverTargetBounds(anchor, event)
      handleRailNamePointer({ anchor, label }, event)
    },
    onMouseMove: (event: MouseEvent<HTMLElement>) =>
      handleRailNamePointer({ anchor, label }, event),
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      clearRailNameTimeout()
      activeRailNameRef.current = null
      // Onto its name card (Home's, with its pencil): still named there.
      if (isInNameCard(event.relatedTarget)) return
      onRailNameLeave(anchor)
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (nameOnFocus && event.currentTarget.matches(':focus-visible')) {
        clearHoverIntent()
        activateRailName({ anchor, label })
      }
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      clearRailNameTimeout()
      activeRailNameRef.current = null
      if (isInNameCard(event.relatedTarget)) return
      onRailNameLeave(anchor)
    },
  })
  useEffect(() => clearRailNameTimeout, [])

  // A row holds its tile at its top, inset from the window's edge and the
  // page, with the gap to the next tile below it, so the tiles line up with
  // the page card's top and bottom. The whole row takes the pointer and click.
  const navItemClass =
    'group flex h-[var(--sidebar-row-height)] w-full min-w-0 shrink-0 select-none px-[var(--sidebar-item-inset)] pb-[var(--sidebar-item-inset)]'
  // The selected item stays blue when hovered or opened, a little deeper
  // (--sidebar-item-active-hover-bg); any other turns neutral.
  const tileClass = (state: 'open' | 'selected-open' | 'selected' | 'idle') =>
    `flex h-full w-full min-w-0 items-center rounded-md pr-2 transition-[background-color,color] duration-200 ${
      state === 'open'
        ? 'bg-[color:var(--sidebar-item-open-bg)] text-default'
        : state === 'selected-open'
          ? 'bg-[color:var(--sidebar-item-active-hover-bg)] text-[color:var(--sidebar-item-active-text)]'
          : state === 'selected'
            ? 'bg-[color:var(--sidebar-item-active-bg)] text-[color:var(--sidebar-item-active-text)] group-hover:bg-[color:var(--sidebar-item-active-hover-bg)]'
            : 'text-subtle group-hover:bg-[color:var(--sidebar-item-hover-bg)]'
    }`

  const navItemTileClass = (module: ActiveModule) => {
    const selected = isSelected(module)
    if (isHovered(module) || namedItem === module) {
      return tileClass(selected ? 'selected-open' : 'open')
    }
    return tileClass(selected ? 'selected' : 'idle')
  }

  // The icon's column: the collapsed rail's width less the tile's inset.
  const iconColumnClass =
    'flex h-full w-[calc(var(--sidebar-width-collapsed)-var(--sidebar-item-inset)*2)] shrink-0 items-center justify-center'

  const labelClass = `overflow-hidden whitespace-nowrap text-sm font-medium transition-[max-width,opacity] duration-200 ${
    isExpanded
      ? 'max-w-[calc(var(--sidebar-width-expanded)-var(--sidebar-width-collapsed)-(var(--sidebar-margin)*2))] opacity-100'
      : 'max-w-0 opacity-0'
  }`

  // A row that opens something beside it ends in a chevron: an area's menu,
  // the search, or Home's or AI's name (over a pinned card, blank meanwhile).
  // Brighter while that's open (`open`), and in the selected blue on the
  // current area's row.
  const chevronClass = (selected: boolean, open: boolean) =>
    // Collapsed, the tile is all icon and the chevron would land in the
    // rail's inset beside it, so it shows only once the rail widens.
    `ml-auto shrink-0 transition-[opacity,color] duration-200 ${
      !isExpanded
        ? 'opacity-0 text-muted'
        : open
          ? selected
            ? 'opacity-100 text-[color:var(--sidebar-item-active-text)]'
            : 'opacity-100 text-default'
          : selected
            ? 'opacity-70 text-[color:var(--sidebar-item-active-text)]'
            : 'opacity-55 text-muted'
    }`

  const menuChevron = (selected: boolean, open: boolean) => (
    <span className={chevronClass(selected, open)} aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="block">
        <path
          d="M5 3.5L8.5 7L5 10.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
  const chevron = (module: ActiveModule) => menuChevron(isSelected(module), isHovered(module))

  // An item with a menu opens it on hover (after the intent delay once the
  // rail is wide, or a rest while another menu is open). A click (or Enter) opens it at once with the cursor in it:
  // in its search, or on its first page (GlobalSidebar, ModuleDrawer). The
  // AI Assistant's click starts a new chat; Enter still opens its menu.
  const menuItemHandlers = (module: ActiveModule) => ({
    [RAIL_MODULE_ATTRIBUTE]: module,
    type: 'button' as const,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isHovered(module),
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      cacheHoverTargetBounds(module, event)
      handleModulePointer(module, event)
    },
    onMouseMove: (event: MouseEvent<HTMLElement>) => handleModulePointer(module, event),
    onMouseLeave: clearHoverIntent,
    onClick: (event: MouseEvent<HTMLElement>) => {
      clearHoverIntent()
      onModuleClick(module, { keyboard: isKeyboardClick(event) })
    },
  })

  const renderModule = (module: AppNavigationModule) => {
    const rowClass = navItemClass
    const ModuleIcon = module.icon
    const contents = (
      <span className={navItemTileClass(module.id)}>
        <span className={iconColumnClass}>
          {module.id === 'me' ? (
            <MeAvatar />
          ) : (
            <ModuleIcon className="h-sidebar-module-size w-sidebar-module-size shrink-0" />
          )}
        </span>
        {/* Me is named for who is signed in. */}
        <span className={labelClass}>{module.id === 'me' ? <MeName /> : module.label}</span>
        {isExpanded && (
          <MenuDescription description={module.description} placement="default" className="ml-2" />
        )}
        {module.drawer === 'none'
          ? menuChevron(isSelected(module.id), namedItem === module.id)
          : chevron(module.id)}
      </span>
    )

    if (module.drawer === 'none') {
      return (
        <NavLink
          key={module.id}
          {...{ [RAIL_MODULE_ATTRIBUTE]: module.id }}
          to={module.sections[0]?.items[0]?.to ?? module.pathPrefix}
          {...menulessItemHandlers(module.id, module.label)}
          className={rowClass}
        >
          {contents}
        </NavLink>
      )
    }

    return (
      <button key={module.id} {...menuItemHandlers(module.id)} className={rowClass}>
        {contents}
      </button>
    )
  }

  const gapHandlers = (keepMeOpen = false) => ({
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      cacheHoverTargetBounds('buffer', event)
      handleGapPointer(event, keepMeOpen)
    },
    onMouseMove: (event: MouseEvent<HTMLElement>) => handleGapPointer(event, keepMeOpen),
    onMouseLeave: clearHoverIntent,
  })

  // A quiet line between groups (the muted sidebar line), spaced like the gap
  // between two tiles. It runs from the icons' left edge, inset from a tile's
  // ends by the icon's margin in it, so on an icons-only rail it's as wide as
  // an icon; widened, only its right end moves with the rail.
  const groupDivider = (
    <div
      role="separator"
      className="shrink-0 px-[calc(var(--sidebar-item-inset)+(var(--sidebar-tile-size)-var(--sidebar-module-size))/2)] pb-[var(--sidebar-item-inset)]"
      {...gapHandlers()}
    >
      <div className="h-px bg-[color:var(--sidebar-border-muted)]" />
    </div>
  )

  // Search, first: the sidebar's search of pages, tables and views
  // (SidebarSearch), in a card beside the rail. A rest opens it, as an area's
  // menu, without taking the cursor; a click opens it with the cursor in its
  // box, and keeps it open. Its chevron: it opens something beside it.
  const searchItem = (
    <button
      type="button"
      {...{ [RAIL_MODULE_ATTRIBUTE]: SEARCH_RAIL_ITEM }}
      {...menulessItemHandlers(SEARCH_RAIL_ITEM, 'Search', { nameOnFocus: false })}
      aria-haspopup="dialog"
      aria-expanded={searchOpen}
      onClick={onSearchClick}
      className={navItemClass}
    >
      <span className={tileClass(searchOpen ? 'open' : 'idle')}>
        <span className={iconColumnClass}>
          <SearchIcon className="h-sidebar-module-size w-sidebar-module-size shrink-0" />
        </span>
        <span className={labelClass}>Search</span>
        {menuChevron(false, searchOpen)}
      </span>
    </button>
  )

  // Home is a shortcut to the user's Home Page, not an area of its own: it
  // never shows as the current one, and it has no menu (its name shows beside
  // it instead).
  const homeItem = (
    <Link
      to={Path.home}
      {...{ [RAIL_MODULE_ATTRIBUTE]: 'home' }}
      {...menulessItemHandlers('home', 'Home')}
      className={navItemClass}
    >
      <span className={tileClass(namedItem === 'home' ? 'open' : 'idle')}>
        <span className={iconColumnClass}>
          <HomeIcon className="h-sidebar-module-size w-sidebar-module-size shrink-0" />
        </span>
        <span className={labelClass}>Home</span>
        {menuChevron(false, namedItem === 'home')}
      </span>
    </Link>
  )

  // Favorites, beside Home: the user's own shortcuts, with a menu of the pages
  // they hearted (FavoritesMenu).
  const favoritesItem = (
    <button {...menuItemHandlers(FAVORITES_RAIL_ITEM)} className={navItemClass}>
      <span className={navItemTileClass(FAVORITES_RAIL_ITEM)}>
        <span className={iconColumnClass}>
          <HeartIcon className="h-sidebar-module-size w-sidebar-module-size shrink-0" />
        </span>
        <span className={labelClass}>Favorites</span>
        {chevron(FAVORITES_RAIL_ITEM)}
      </span>
    </button>
  )

  const workModules = modules.filter((module) => module.railGroup === 'work')
  const configurationModules = modules.filter((module) => module.railGroup === 'configuration')
  const accountModules = modules.filter((module) => module.railGroup === 'account')

  return (
    <aside
      // Every move over the rail, before the item under it reads the trail.
      onMouseMoveCapture={recordPointer}
      onMouseLeave={() => {
        pointerTrailRef.current = []
      }}
      className={`relative flex h-full flex-col overflow-x-hidden bg-[var(--surface-sidebar)] transition-[width] duration-200 ${
        isExpanded ? 'w-sidebar-width-expanded' : 'w-sidebar-width-collapsed'
      }`}
    >
      <nav className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto py-0">
        {/* The cards' gap from the header. */}
        <div
          className="h-[var(--page-card-gap-top)] shrink-0"
          onMouseEnter={() => onModuleHover(null)}
        />
        {groupDivider}
        <div className="flex flex-col items-stretch">{searchItem}</div>
        {groupDivider}
        <div className="flex flex-col items-stretch">
          {homeItem}
          {favoritesItem}
        </div>
        {groupDivider}
        <div className="flex flex-col items-stretch">
          {workModules.map((module) => renderModule(module))}
        </div>
        {workModules.length > 0 && configurationModules.length > 0 && groupDivider}
        {configurationModules.length > 0 && (
          <div className="flex flex-col items-stretch">
            {configurationModules.map((module) => renderModule(module))}
          </div>
        )}

        <div data-sidebar-account-gap className="flex-1" {...gapHandlers(true)} />

        {accountModules.length > 0 && (
          <div className="flex flex-col items-stretch">
            {accountModules.map((module) => renderModule(module))}
          </div>
        )}
        {groupDivider}
        <SidebarModeControl isExpanded={isExpanded} onOpenChange={onExpansionOpenChange} />
      </nav>
    </aside>
  )
}
